import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import BalanceIcon from "@mui/icons-material/Balance";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import InfoIcon from "@mui/icons-material/Info";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

const KthPatentLitigationSupport = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <BalanceIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          Patent Litigation Support
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              While our firm does not practice as patent litigators, we provide
              specialized technical and analytical support to litigation teams
              handling patent disputes in District Courts, the Patent Trial and
              Appeal Board (PTAB), and the International Trade Commission (ITC).
              Our deep engineering and patent expertise makes us valuable
              technical advisors for litigation counsel.
            </Typography>

            <Typography variant="body1" paragraph>
              Our team's combination of technical knowledge, patent expertise,
              and litigation support experience enables us to assist litigation
              teams with the complex technical aspects of patent disputes across
              various engineering and technology domains.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Our Litigation Support Services:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Infringement/Non-infringement Analysis"
                    secondary="Technical assessment of how accused products or services map to patent claims"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Invalidity Theory Development"
                    secondary="Prior art searching and analysis to support validity challenges"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Claim Construction Support"
                    secondary="Technical analysis to support Markman hearing positions"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Detailed Claim Charts"
                    secondary="Preparation of infringement or invalidity claim charts with technical analysis"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expert Witness Collaboration"
                    secondary="Technical support for expert witnesses and expert report development"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="PTAB Proceeding Support"
                    secondary="Technical assistance for IPR, PGR, and other post-grant proceedings"
                  />
                </ListItem>
              </List>
            </Box>

            <InfoBox
              title="Note About Our Role"
              content={
                <Typography variant="body2">
                  We work collaboratively with litigation counsel as technical
                  advisors rather than as litigation counsel. Our role is to
                  provide the deep technical expertise and patent analytical
                  skills that complement your litigation team's legal strategy.
                  This collaborative approach allows us to focus on our core
                  strength – the intersection of technology and patent law –
                  while your litigation counsel manages the overall case
                  strategy and court proceedings.
                </Typography>
              }
              type="info"
              icon={<InfoIcon />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={getImageUrl("kth-body-3-litsupp-tit.png")}
                alt="Patent Litigation Support"
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography variant="h6" gutterBottom color={tertColor}>
                Technical Expertise Across Forums
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "white",
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      height: "100%",
                    }}
                  >
                    <GavelIcon sx={{ fontSize: "2.5rem", color: tertColor }} />
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      District Courts
                    </Typography>
                    <Typography variant="body2">
                      Technical support for patent infringement litigation
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "white",
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      height: "100%",
                    }}
                  >
                    <AssignmentIcon
                      sx={{ fontSize: "2.5rem", color: tertColor }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      PTAB
                    </Typography>
                    <Typography variant="body2">
                      Technical assistance for IPR and PGR proceedings
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "white",
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      height: "100%",
                    }}
                  >
                    <FactCheckIcon
                      sx={{ fontSize: "2.5rem", color: tertColor }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      ITC
                    </Typography>
                    <Typography variant="body2">
                      Technical support for Section 337 investigations
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Our Qualifications
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={1}>
                    <WorkIcon sx={{ color: tertColor, mr: 1, mt: 0.5 }} />
                    <Typography variant="body2">
                      Our attorneys have engineering degrees and professional
                      experience across electrical, computer, and mechanical
                      disciplines
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={1}>
                    <SchoolIcon sx={{ color: tertColor, mr: 1, mt: 0.5 }} />
                    <Typography variant="body2">
                      Registered patent attorneys with USPTO practice experience
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={1}>
                    <PersonIcon sx={{ color: tertColor, mr: 1, mt: 0.5 }} />
                    <Typography variant="body2">
                      Previous collaboration with litigation teams on complex
                      patent matters
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" mb={1}>
                    <InfoIcon sx={{ color: tertColor, mr: 1, mt: 0.5 }} />
                    <Typography variant="body2">
                      Expertise in standards-essential patents and industry
                      standards
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Technical Areas of Expertise */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: tertColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Technical Areas of Expertise
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${tertColor}33`,
                  borderLeft: `4px solid ${tertColor}`,
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Wireless & Telecommunications
                </Typography>
                <Typography variant="body2" paragraph>
                  We provide technical expertise for patent litigation involving
                  cellular technologies, Wi-Fi standards, wireless protocols,
                  antenna systems, RF circuits, and telecommunications
                  infrastructure.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" label="5G/LTE" />
                  <Chip size="small" label="Wi-Fi" />
                  <Chip size="small" label="Bluetooth" />
                  <Chip size="small" label="IoT" />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${tertColor}33`,
                  borderLeft: `4px solid ${tertColor}`,
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Computer Systems & Software
                </Typography>
                <Typography variant="body2" paragraph>
                  We support litigation involving computer hardware,
                  semiconductor technologies, memory systems, software methods,
                  networking technologies, and cloud computing architectures.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" label="Processors" />
                  <Chip size="small" label="Memory" />
                  <Chip size="small" label="Software" />
                  <Chip size="small" label="Networking" />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${tertColor}33`,
                  borderLeft: `4px solid ${tertColor}`,
                  borderRadius: 1,
                  height: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Artificial Intelligence & Machine Learning
                </Typography>
                <Typography variant="body2" paragraph>
                  We provide technical analysis for litigation involving AI
                  systems, neural networks, machine learning algorithms,
                  computer vision, natural language processing, and data science
                  patents.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  <Chip size="small" label="Neural Networks" />
                  <Chip size="small" label="Computer Vision" />
                  <Chip size="small" label="NLP" />
                  <Chip size="small" label="ML Algorithms" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Case Study/Success Story */}
        <Box sx={{ mt: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            color={tertColor}
          >
            Example Litigation Support Case
          </Typography>
          <Typography variant="body2" paragraph>
            We provided technical support to a litigation team representing a
            telecommunications equipment manufacturer in a patent infringement
            case involving cellular base station technology. Our contributions
            included:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <ChevronRightIcon sx={{ color: tertColor }} />
              </ListItemIcon>
              <ListItemText primary="Detailed analysis of complex 5G cellular standard specifications relevant to the patents-in-suit" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ChevronRightIcon sx={{ color: tertColor }} />
              </ListItemIcon>
              <ListItemText primary="Development of technical arguments for claim construction positions" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ChevronRightIcon sx={{ color: tertColor }} />
              </ListItemIcon>
              <ListItemText primary="Preparation of comprehensive infringement claim charts mapping patent claims to industry standards" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ChevronRightIcon sx={{ color: tertColor }} />
              </ListItemIcon>
              <ListItemText primary="Support for expert witness testimony preparation on technical issues" />
            </ListItem>
          </List>
          <Typography variant="body2">
            Our technical expertise helped the litigation team secure a
            favorable settlement for the client before trial.
          </Typography>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: quadColor,
              color: "white",
              "&:hover": {
                backgroundColor: quadHoverColor,
              },
              mt: 1,
            }}
            onClick={() => {
              const contactSection = document.getElementById("contact-section");
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            Discuss Your Patent Litigation Support Needs
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KthPatentLitigationSupport;
